<!--
 * Path: src/features/browser/forms/link/LinkRead.vue
 * Task: Displays a link field in read-only mode, using SubHeader (conditionally) and chips for link.
 * Update: Fixed double header rendering by properly mapping the hideHeader prop to the SubHeader v-if.
-->
<template>
  <div :class="{ 'mb-6': !hideHeader }">
    <!-- Header conditionally rendered -->
    <SubHeader
      v-if="!hideHeader"
      :title="field.name"
      :info="field.info"
      @click:info="$emit('show-info')"
    />

    <!-- Content -->
    <div class="ms-1">
      <div v-if="!hasLinkData" class="text-body-2 text-disabled">
        -
      </div>
      <v-chip
        v-else
        color="primary"
        variant="tonal"
        prepend-icon="mdi-link-variant"
        @click="openLink"
        link
        label
      >
        {{ displayLabel }}
        <!-- Tooltip showing full URL -->
        <v-tooltip activator="parent" location="top" open-delay="300">
          {{ linkUrl }}
        </v-tooltip>
        <!-- External link icon -->
        <v-icon end size="small" icon="mdi-open-in-new" class="ms-2"></v-icon>
      </v-chip>
    </div>
  </div>
</template>

<script setup lang="ts">
/**
 * Path: src/features/browser/forms/link/LinkRead.vue
 * Task: Controller for link rendering in read-only panels.
 */
import { computed, inject } from 'vue';
import { useWorkspaceStore } from '@/store/workspace';
import { FormContextKey } from '@/core/keys';
import type { Entity } from '@/core/types/editor';
import SubHeader from '../SubHeader.vue';

interface FieldDef {
  id: string;
  name: string;
  info?: string;
}

const props = defineProps<{
  field: FieldDef;
  hideHeader?: boolean;
}>();

defineEmits<{
  (e: 'show-info'): void;
}>();

const workspaceStore = useWorkspaceStore();
const formContext = inject(FormContextKey, null);

const activeEntity = computed<Entity | null>(() => formContext ? formContext.entity.value : workspaceStore.selectedEntity);

const linkData = computed<{ url?: string; name?: string } | null>(() => {
  if (!activeEntity.value) return null;
  return (activeEntity.value as any)[props.field.id] as { url?: string; name?: string } | null;
});

const hasLinkData = computed<boolean>(() => {
  return !!(linkData.value && linkData.value.url);
});

const linkUrl = computed<string>(() => {
  return linkData.value?.url || '';
});

const displayLabel = computed<string>(() => {
  return linkData.value?.name || linkUrl.value;
});

const openLink = (): void => {
  if (!linkUrl.value) return;
  let target = linkUrl.value;
  if (!/^https?:\/\//i.test(target)) {
    target = 'https://' + target;
  }
  window.open(target, '_blank');
};
</script>
